"use client";

import * as React from "react";
import * as RechartsPrimitive from "recharts";

import { cn } from "@/lib/utils";

export type ChartConfig = {
  [key: string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: string;
  };
};

type ChartContextValue = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextValue | null>(null);

function useChart() {
  const chart = React.useContext(ChartContext);

  if (!chart) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return chart;
}

function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}: React.ComponentProps<"div"> & {
  config: ChartConfig;
  children: React.ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >["children"];
}) {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          "flex aspect-video min-h-[240px] w-full justify-center text-xs",
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground",
          "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50",
          "[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border",
          "[&_.recharts-dot[stroke='#fff']]:stroke-transparent",
          "[&_.recharts-layer]:outline-none",
          "[&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border",
          "[&_.recharts-radial-bar-background-sector]:fill-muted",
          "[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted",
          "[&_.recharts-reference-line_[stroke='#ccc']]:stroke-border",
          className,
        )}
        style={
          Object.fromEntries(
            Object.entries(config).map(([key, item]) => [
              `--color-${key}`,
              item.color ?? "var(--chart-1)",
            ]),
          ) as React.CSSProperties
        }
        {...props}
      >
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

const ChartTooltip = RechartsPrimitive.Tooltip;

type ChartTooltipContentProps = {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    name?: string | number;
    value?: unknown;
    color?: string;
    payload?: unknown;
  }>;
  className?: string;
  label?: React.ReactNode;
  labelFormatter?: (
    label: React.ReactNode,
    payload: ChartTooltipContentProps["payload"],
  ) => React.ReactNode;
  formatter?: (
    value: unknown,
    name: unknown,
    item: {
      dataKey?: string | number;
      name?: string | number;
      value?: unknown;
      color?: string;
      payload?: unknown;
    },
    index: number,
    payload: ChartTooltipContentProps["payload"],
  ) => React.ReactNode;
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: "line" | "dot";
};

function ChartTooltipContent({
  active,
  payload,
  className,
  label,
  labelFormatter,
  formatter,
  hideLabel = false,
  hideIndicator = false,
  indicator = "dot",
}: ChartTooltipContentProps) {
  const { config } = useChart();

  if (!active || !payload?.length) {
    return null;
  }

  const tooltipLabel = hideLabel
    ? null
    : (labelFormatter?.(label, payload) ?? label);

  return (
    <div
      className={cn(
        "grid min-w-40 items-start gap-1.5 rounded-lg border bg-background px-2.5 py-2 text-xs shadow-xl",
        className,
      )}
    >
      {tooltipLabel != null ? (
        <div className="font-medium text-foreground">{tooltipLabel as React.ReactNode}</div>
      ) : null}
      <div className="grid gap-1">
        {payload.map((item, index) => {
          const key = String(item.dataKey ?? item.name ?? index);
          const itemConfig = config[key];
          const itemColor =
            (typeof item.color === "string" ? item.color : undefined) ??
            itemConfig?.color ??
            "var(--chart-1)";
          const itemLabel = itemConfig?.label ?? item.name;

          return (
            <div key={key} className="flex items-center gap-2">
              {!hideIndicator ? (
                indicator === "line" ? (
                  <div
                    className="h-0.5 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: itemColor }}
                  />
                ) : (
                  <div
                    className="size-2 shrink-0 rounded-[2px]"
                    style={{ backgroundColor: itemColor }}
                  />
                )
              ) : null}
              <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                <span className="truncate text-muted-foreground">
                  {itemLabel as React.ReactNode}
                </span>
                <span className="font-mono tabular-nums text-foreground">
                  {formatter
                    ? formatter(item.value, item.name, item, index, payload)
                    : (item.value as React.ReactNode)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ChartLegend = RechartsPrimitive.Legend;

function ChartLegendContent({
  payload,
  className,
}: {
  payload?: Array<{ dataKey?: string | number; value?: React.ReactNode; color?: string }>;
  className?: string;
}) {
  const { config } = useChart();

  if (!payload?.length) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground",
        className,
      )}
    >
      {payload.map((item) => {
        const key = String(item.dataKey ?? item.value ?? "");
        const itemConfig = config[key];
        const color =
          (typeof item.color === "string" ? item.color : undefined) ??
          itemConfig?.color ??
          "var(--chart-1)";
        const label = itemConfig?.label ?? item.value;

        return (
          <div key={key} className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-[2px]"
              style={{ backgroundColor: color }}
            />
            <span>{label as React.ReactNode}</span>
          </div>
        );
      })}
    </div>
  );
}

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
};
